import {
  Box,
  Checkbox,
  Stack,
  Tooltip,
  Typography,
} from '@mui/material';
import { Category as SemiIcon, Texture as FabricIcon } from '@mui/icons-material';
import {
  flattenSlotGroups,
  flattenSlots,
  fmt,
  groupSlotsBySize,
  modsLabel,
  planTargetId,
} from './model';
import TechcardSelect from './TechcardSelect';

const ROLES = {
  fabric: { label: 'Ткань', Icon: FabricIcon, color: 'secondary' },
  semi: { label: 'Полуфабрикат', Icon: SemiIcon, color: 'info' },
};

function roleOf(role) {
  return ROLES[role] || ROLES.semi;
}

function memberHint(node) {
  const count = Array.isArray(node.members) ? node.members.length : 0;
  if (count < 2) return '';
  return `${node.name}: ${modsLabel(count)} в этой ветке, остаток — их сумма`;
}

function SlotRow({ row, savingPlanId, onToggle, onPlanChange }) {
  const { node, depth, included, notCounted, dirty, showRuleHint } = row;
  const role = roleOf(node.role);
  const muted = notCounted ? 0.55 : 1;
  return (
    <Box>
      {showRuleHint && (
        <Typography
          variant="caption"
          color="warning.main"
          sx={{ display: 'block', pl: 1 + depth * 2.5, pt: 1 }}
        >
          Правило не задано: добавка не считается
        </Typography>
      )}
      <Box
        sx={{
          display: 'grid',
          gridTemplateColumns: {
            xs: '1fr',
            md: 'minmax(0, 1fr) auto auto minmax(220px, 280px)',
          },
          columnGap: 2,
          rowGap: 0.5,
          alignItems: 'center',
          py: 0.5,
          pl: 1 + depth * 2.5,
          borderBottom: 1,
          borderColor: 'divider',
        }}
      >
        <Stack direction="row" spacing={0.5} alignItems="center" sx={{ minWidth: 0 }}>
          <Checkbox
            size="small"
            checked={included}
            color={dirty ? 'warning' : 'primary'}
            onChange={() => onToggle(node)}
            inputProps={{ 'aria-label': `Учитывать ${node.name}` }}
            sx={dirty ? { color: 'warning.main', '&.Mui-checked': { color: 'warning.main' } } : undefined}
          />
          <Tooltip title={role.label}>
            <role.Icon fontSize="small" color={role.color} sx={{ opacity: muted }} />
          </Tooltip>
          <Box sx={{ minWidth: 0, opacity: muted }}>
            <Typography variant="body2" noWrap title={memberHint(node) || node.name}>
              {node.name}
            </Typography>
            {notCounted && (
              <Typography variant="caption" color="text.secondary" display="block">
                не считается
              </Typography>
            )}
          </Box>
        </Stack>
        <Typography variant="body2" color="text.secondary" sx={{ opacity: muted, whiteSpace: 'nowrap' }}>
          ×{fmt(node.qty_example)} {node.unit || ''}
        </Typography>
        <Typography variant="body2" sx={{ opacity: muted, whiteSpace: 'nowrap' }}>
          {fmt(node.stock)} {node.unit || ''}
        </Typography>
        <TechcardSelect
          node={node}
          targetId={planTargetId(node)}
          saving={Boolean(savingPlanId)}
          onChange={onPlanChange}
        />
      </Box>
    </Box>
  );
}

export default function FamilySlotTree({
  tree,
  familyChildren,
  savedRules,
  draftRules,
  savingPlanId,
  onToggle,
  onPlanChange,
}) {
  if (!tree?.length) {
    return (
      <Typography variant="body2" color="text.secondary">
        В техкартах семьи нет материалов — отмечать нечего.
      </Typography>
    );
  }

  const bySize = groupSlotsBySize(tree, familyChildren);
  const sections = bySize
    ? flattenSlotGroups(bySize, savedRules, draftRules)
    : [{ label: null, rows: flattenSlots(tree, savedRules, draftRules) }];

  return (
    <Box>
      {sections.map((section) => (
        <Box key={section.label ?? 'all'}>
          {section.label && (
            <Typography
              variant="overline"
              color="text.secondary"
              component="div"
              sx={{ px: 1, pt: 1.5, lineHeight: 2, borderBottom: 1, borderColor: 'divider' }}
            >
              {section.label}
            </Typography>
          )}
          {section.rows.map((row, index) => (
            <SlotRow
              key={`${index}-${row.node.key}`}
              row={row}
              savingPlanId={savingPlanId}
              onToggle={onToggle}
              onPlanChange={onPlanChange}
            />
          ))}
        </Box>
      ))}
    </Box>
  );
}
